import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useSFX } from 'js/hooks/useSFX';

// import components
import SwiperVirtual from 'js/components/swiper/Swiper.virtual';
import ActivityCard from 'js/components/activity-card/ActivityCard';

// import icons
import IconHeart from 'js/components/icon/Icon.heart';

// import selectors
import { getTranslationById } from 'js/store/app/App.selectors';

// import actions
import { toggleFavoriteCuratedCollection } from 'js/store/user/User.actions.js';
import { trackEvent } from '../../utils/tracking';

const ActivityCardCarousel = ({ title, cards, url, id = false, type = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { playSFX } = useSFX();

  const renderSlider = () => {
    const slides =
      !cards || cards?.length === 0
        ? [
            <CardPlaceholderSlide key="placeholder-1" url={url} />,
            <CardPlaceholderSlide key="placeholder-2" url={url} />,
            <CardPlaceholderSlide key="placeholder-3" url={url} />,
          ]
        : cards?.map((card) => {
            if (!card) return null;
            return (
              <div
                style={{ cursor: 'pointer' }}
                key={card.id}
                onClick={(e) => {
                  if (
                    e.target.classList.value.includes('card') ||
                    e.target.parentNode.classList.value.includes('card')
                  ) {
                    playSFX('button_tap');
                    navigate(url, { state: { cardID: card.id } });
                  }
                }}
              >
                <ActivityCard allowFlip={false} data={card} />
              </div>
            );
          });

    if (
      (type === 'curated' && slides.length === 5) ||
      (type === 'all-cards' && slides.length === 8) ||
      (type !== 'all-cards' && slides.length === 20)
    ) {
      slides.push(<CardViewAllSlide key="view-all" url={url} />);
    }

    return <SwiperVirtual width={185} slides={slides} />;
  };

  const curatedCollectionTitle = () => {
    const favorited = useSelector((state) => {
      return state?.user?.favoriteCuratedCollections?.includes(id) || false;
    });

    const addToFavorites = (e) => {
      playSFX('hearting_a_card_feedback');
      e.stopPropagation();
      e.preventDefault();
      trackEvent(favorited ? 'game_collection_unfavorite' : 'game_collection_favorite', {
        collection_id: id,
      });
      dispatch(toggleFavoriteCuratedCollection(id));
    };

    return (
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button
          className="card__icon card__icon--favorite card__icon--favorite-curated"
          onClick={addToFavorites}
          aria-label="Add to favorite curated collections"
          style={{ position: 'relative', top: 0, left: 0 }}
        >
          <IconHeart active={favorited} />
        </button>
        <h2 style={{ margin: 0 }}>{title}</h2>
      </div>
    );
  };

  return (
    <div>
      {type === 'curated' ? curatedCollectionTitle() : <h2>{title}</h2>}
      {renderSlider()}
    </div>
  );
};

const CardPlaceholderSlide = ({ url }) => {
  const siteText = {
    recentlyViewedCards: useSelector((state) =>
      getTranslationById(
        state,
        'default_text_recently_viewed_cards_will_appear_here',
        'Recently viewed cards will appear here.'
      )
    ),
    savedFavorites: useSelector((state) =>
      getTranslationById(state, 'default_text_saved_favorites_will_appear_here', 'Saved Favorites will appear here.')
    ),
  };

  const text = url == '/recent' ? siteText.recentlyViewedCards : siteText.savedFavorites;
  return (
    <div className="placeholder-slide--card">
      <button className="card__icon card__icon--favorite card__icon--favorite-curated">
        <IconHeart active={false} />
      </button>
      <p>{text}</p>
    </div>
  );
};

const CardViewAllSlide = ({ url }) => {
  const navigate = useNavigate();
  const { playSFX } = useSFX();

  return (
    <div
      className="placeholder-slide--card"
      onClick={() => {
        playSFX('button_tap');
        navigate(url);
      }}
      style={{ cursor: 'pointer' }}
    >
      <p>View All</p>
    </div>
  );
};

export default ActivityCardCarousel;
